import { useAppDispatch } from "hooks/hooks";
import {
  updateRequisition
} from "../requisition.slice";
import Requisition from "./Requisition";
import "./../styles/main.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DeleteRequisitionModal from "./DeleteRequisitionModal";
import { requisitionsService } from "../services/requisitions.service";
import { toast } from "react-toastify";
import { setAcceptedRequisitionsNumber } from "Dashboard/dashboard.slice";
import { debounce } from "lodash";
import { IRequisition } from "../domain/IRequisition";
import Loader from "components/Loader/Loader";
import ProjectSelect from "Dashboard/scenes/Projects/components/ProjectSelect";
import { useProjectOptions } from "Dashboard/hooks/ProjectsOptions";
import { BiSearchAlt } from "react-icons/bi";
import { styles as projectSelectStyles } from "components/Popover/popoverSelectStyle";
import usePagingAndSort, { IGetPageRequestParams, IUpdateStateAfterSearchParams } from "hooks/pagingAndSort";
import { IGetRequisitionsRequestParams } from "../domain/IGetRequisitionsRequestParams";

interface IProps {
  setClickOutsideActive: React.Dispatch<React.SetStateAction<boolean>>;
};

const RequisitionsTooltip = ({ setClickOutsideActive }: IProps) => {
  const dispatch = useAppDispatch();
  const projects = useProjectOptions({ showHiddenInDropDowns: false });

  const [getRequisitionsRequestParams, setGetRequisitionsRequestParams] = useState<IGetRequisitionsRequestParams>({});
  const [requisitions, setRequisitions] = useState<IRequisition[]>([]);
  const limit = 4;

  const getRequisitionsWithPaging = useCallback(async (
    pagingParams: IGetPageRequestParams,
    abortSignal: AbortSignal,
    updateStateAfterFetch: (updateStateParams: IUpdateStateAfterSearchParams) => void
  ): Promise<void> => {
    const fetchedRequisitions = await requisitionsService.getAllRequsitionsV2(getRequisitionsRequestParams, pagingParams, abortSignal);
    if (pagingParams.fetchMode === 'replace') {
      const elementsFetched = fetchedRequisitions.length;
      const newFirstElementId = fetchedRequisitions?.[0]?.requisitionId;
      const newLastElementId = fetchedRequisitions?.[fetchedRequisitions.length - 1]?.requisitionId;
      setRequisitions(fetchedRequisitions);
      updateStateAfterFetch({
        fetchSide: "replace",
        newFirstElementId,
        newLastElementId,
        elementsFetched
      });
    } else if (pagingParams.beforeId) {
      setRequisitions((prevRequisitions) => {
        const elementsFetched = fetchedRequisitions.length;
        const elementsFetchedIds = fetchedRequisitions.map(item => item.requisitionId);
        const prevRequisitionsFiltered = prevRequisitions.filter(item => !elementsFetchedIds.includes(item.requisitionId));
        const newRequisitions = [...fetchedRequisitions, ...prevRequisitionsFiltered];
        const newFirstElementId = newRequisitions?.[0]?.requisitionId;
        const newLastElementId = newRequisitions?.[newRequisitions.length - 1]?.requisitionId;
        updateStateAfterFetch({
          fetchSide: "beforeFirst",
          newFirstElementId,
          newLastElementId,
          elementsFetched
        });
        return newRequisitions;
      });
    } else {
      setRequisitions((prevRequisitions) => {
        const elementsFetched = fetchedRequisitions.length;
        const elementsFetchedIds = fetchedRequisitions.map(item => item.requisitionId);
        const prevRequisitionsFiltered = prevRequisitions.filter(item => !elementsFetchedIds.includes(item.requisitionId));
        const newRequisitions = [...prevRequisitionsFiltered, ...fetchedRequisitions];
        const newFirstElementId = newRequisitions?.[0]?.requisitionId;
        const newLastElementId = fetchedRequisitions?.[elementsFetched - 1]?.requisitionId;
        updateStateAfterFetch({
          fetchSide: "afterLast",
          newFirstElementId,
          newLastElementId,
          elementsFetched
        });
        return newRequisitions;
      });
    }
  }, [dispatch, getRequisitionsRequestParams]);

  const { resetAfterSearch, lastElementRef, handleScroll } =
    usePagingAndSort(
      getRequisitionsWithPaging,
      limit,
    );

  useEffect(() => {
    resetAfterSearch();
  }, [getRequisitionsRequestParams]);

  const selectProject = (projectId: number | undefined) => {
    setGetRequisitionsRequestParams((prevState) => ({
      ...prevState,
      projectId: projectId
    }));
  };

  const doAcceptRequisition = async (requisitionId: number) => {
    try {
      let req = await requisitionsService.acceptRequisition(requisitionId);
      setRequisitions(prevRequisitions => prevRequisitions.map((requisition) =>
        requisition.requisitionId === req.requisitionId
          ? { ...requisition, isAccepted: true }
          : { ...requisition }));
      dispatch(setAcceptedRequisitionsNumber(true));
      toast.success("Uspešno prihvaćeno trebovanje.");
    } catch (err) {
      console.log(err);
    }
  }

  const doReplyRequisition = useMemo(
    () =>
      debounce(async (requisitionId: number, replyText: string) => {
        try {
          if (replyText) {
            let requisition = await requisitionsService.replyToRequisition(requisitionId, replyText);
            dispatch(updateRequisition(requisition));
            toast.success("Poruka je uspešno poslata.");
          }
        } catch (err: any) {
          if (err.response.data.message.startsWith("Employee who is updating reply is not the same one who created reply")) {
            toast.warn("Ne možete uneti ništa u ovo trebovanje jer ga je drugi zaposleni preuzeo.")
          }
        }
      }, 1000),
    [dispatch]
  );

  return (
    <div id="requirements-tooltip" onScroll={handleScroll}>
      {false && <Loader />}
      <div className="requisitions-filter">
        <ProjectSelect
          options={projects}
          onChange={(projectId: number) => {
            selectProject(projectId);
          }}
          placeholder={
            <><BiSearchAlt size={12} /><span>Projekat</span></>
          }
          defaultValue={undefined}
          menuPortalTarget={document.body}
          customStyles={projectSelectStyles.customStyleClassic}
        />
      </div>
      {requisitions.map((requisition, index) => (
        <Requisition
          requisition={requisition}
          index={index}
          key={`${requisition.requisitionId}`}
          doAccept={doAcceptRequisition}
          doReply={doReplyRequisition}
          isLast={index === requisitions.length - 1}
          lastElementRef={lastElementRef}
        />
      ))}
      {false && <Loader />}
      <DeleteRequisitionModal setClickOutsideActive={setClickOutsideActive} />
    </div>
  );
}

export default RequisitionsTooltip;
